import { HttpClient } from '@angular/common/http';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { objectsToArrays } from '../array-fix/functions/object-to-array';
import { Contact } from './contact';
import {
  ServiceProvider as ServiceProviderModel,
  ServiceProviderResponse,
} from '../models/service-provider';
import _ from 'lodash';
import {
  FormAddressStructure,
  FormContactStructure,
  FormServiceProviderServiceStructure,
  FormStructureType,
  FormStructureValueType,
} from '../forms/form-structure';
import { FormGenerator } from '../forms/form-generator';
import { Observable, of } from 'rxjs';
import { easyFormatter } from '../easyFormatter';
import { environment } from 'src/environments/environment';
import { finalize, map, tap } from 'rxjs/operators';
import { serviceVisibilityFormValue } from '../forms/form-values';
import moment from 'moment';
import { arraysToObjects } from '../array-fix/functions/array-to-object';
import { DynamicComponents, DynamicService } from '../dynamic/services/dynamic.service';
import { Service } from './service';
import * as dot from 'dot-object';
import { UserService } from '../services/user.service';
import { User } from './user';
import { SiteService } from '../services/site.service';

export class ServiceProvider {
  isPartial: boolean;
  id: number | null;

  contacts: Contact[] = [];

  form!: FormGroup;
  obj!: { [key: string]: any };

  // Service _meta.ids that exist in the persisted (server-side) provider.
  // Refreshed every time the form is (re)built from server data — on load and
  // after each save. The per-product inline PATCH targets a service by id, so a
  // freshly-added (unsaved) service is NOT in here yet; the inline-save guard
  // uses this to fall back to a whole-provider save for such services.
  private persistedServiceIds = new Set<string>();

  private validatorPaths: string[];

  constructor(
    private siteService: SiteService,
    private http: HttpClient,
    private dynamicService: DynamicService,
    private userService: UserService,
    data?: any
  ) {
    this.id = null;
    this.isPartial = false;

    this.validatorPaths = [];

    if (data) {
      this.id = data.id;

      this.obj = objectsToArrays(data.data);

      const formData = this.postDataAsForm(this.obj as ServiceProviderModel);

      this.initialiseListingForm(formData);
    } else {
      this.initialiseCreateForm();
    }
  }

  get name(): string {
    return this.form.get('title')?.value;
  }

  get address(): any {
    return this.form.get('address')?.value;
  }

  get contact(): any {
    return this.form.get('contact')?.value;
  }

  get isGlobal(): any {
    return !!this.form.get('isGlobal')?.value;
  }

  /**
   * Build the POST payload for a duplicate of THIS provider. Uses the current
   * form value (including any unsaved edits), assigns the supplied title,
   * strips the top-level DB id so the server treats it as a create, and
   * regenerates every nested _meta.id so the duplicate's services / categories
   * / products don't share opaque ids with the original.
   *
   * Returns the payload only — the caller POSTs via ServiceProviderService.
   */
  public buildDuplicatePayload(newTitle: string): any {
    const data = this.formAsPostData();

    delete data.id;
    data.title = newTitle;

    this.regenerateNestedMetaIds(data);

    return data;
  }

  /**
   * Walk the data tree and replace every `_meta.id` with a fresh, unique value.
   * `_meta.index` and any other meta fields are preserved. Idempotent and safe
   * to call on a deep tree.
   */
  private regenerateNestedMetaIds(root: any): void {
    let counter = 0;
    const stamp = Date.now();

    const walk = (node: any) => {
      if (node === null || node === undefined || typeof node !== 'object') return;
      if (Array.isArray(node)) {
        for (const item of node) walk(item);
        return;
      }
      if (node._meta && typeof node._meta === 'object' && typeof node._meta.id === 'string') {
        node._meta.id = `id-${stamp}-${++counter}`;
      }
      for (const key of Object.keys(node)) {
        walk(node[key]);
      }
    };

    walk(root);
  }

  public save(): Observable<boolean | any> {
    if (this.id) {
      return this.update();
    } else {
      return this.create();
    }
  }

  public retrieve(): Observable<boolean> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.getById,
      { id: (this.id as number).toString() }
    );

    this.siteService.addSubscriptionLog(
      this,
      'service-provider.ts->retrieve->this.http.get<ServiceProviderResponse>(url)'
    );

    return this.http.get<ServiceProviderResponse>(url).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'service-provider.ts->retrieve->this.http.get<ServiceProviderResponse>(url)'
        )
      ),
      map((res) => {
        this.obj = objectsToArrays((res.data as ServiceProviderModel).data);

        const formData = this.postDataAsForm(_.cloneDeep(this.obj) as ServiceProviderModel);

        this.initialiseCompleteForm(formData);

        return true;
      })
    );
  }

  public getServiceById(id: string): Service | null {
    const services = this.form.get('services') as FormArray;

    for (var i = 0; i < services.length; i++) {
      const service = services.at(i) as FormGroup;

      const serviceId = service.get('_meta.id')?.value;

      if (id === serviceId) {
        return service.get('service')?.value as Service;
      }
    }

    return null;
  }

  public getServicesByType(type: DynamicComponents): Service[] {
    const serviceByType: Service[] = [];

    const services = this.form.get('services') as FormArray;

    // for (var i = 0; i < services.length; i++) {

    //   const service = services.at(i) as FormGroup;

    //   const serviceId = service.get('_meta.id')?.value;

    //   if (id === serviceId) {

    //     return service.get('service')?.value as Service;

    //   }

    // }

    return serviceByType;
  }

  /**
   * Get the linked user from the form
   */
  public getLinkedUser(): User | null {
    const user = this.form.get('user')?.value;

    if (user) {
      return user as User;
    }

    return null;
  }

  private update(): Observable<any> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.patch,
      { id: (this.id as number).toString() }
    );

    const data = this.formAsPostData();

    const arrayToObject = arraysToObjects(data);

    this.siteService.addSubscriptionLog(
      this,
      'service-provider.ts->update->this.http.patch<any>(url, arrayToObject)'
    );

    return this.http.patch<any>(url, arrayToObject).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'service-provider.ts->update->this.http.patch<any>(url, arrayToObject)'
        )
      ),
      map((res) => {
        this.obj = res.data.service.data;
        this.postDataAsForm(res.data.service.data);

        // The save persisted any newly-added services; refresh the set so their
        // products can use the per-product PATCH path on subsequent edits.
        this.refreshPersistedServiceIds();

        return res;
      })
    );
  }

  private create(): Observable<boolean> {
    const url = environment.api.host + environment.api.paths.api.serviceProvider.post;

    const data = this.formAsPostData();

    const arrayToObject = arraysToObjects(data);

    this.siteService.addSubscriptionLog(
      this,
      'service-provider.ts->create->this.http.post<any>(url, arrayToObject)'
    );

    return this.http.post<any>(url, arrayToObject).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'service-provider.ts->create->this.http.post<any>(url, arrayToObject)'
        )
      ),
      map((res) => {
        this.id = res.data.id;
        this.obj = res.data.data;
        this.postDataAsForm(res.data.data);
        this.refreshPersistedServiceIds();

        return true;
      })
    );
  }

  private initialiseListingForm(data?: any) {
    this.initialiseCompleteForm(data);
  }

  private initialiseCreateForm(data?: any) {
    this.initialiseCompleteForm(data);
  }

  private initialiseCompleteForm(data: any) {
    const formStructure: any = {
      title: { type: FormStructureType.FormControl },
      abn: { type: FormStructureType.FormControl },
      user: { type: FormStructureType.FormControl },
      visibility: { type: FormStructureType.FormControl, value: serviceVisibilityFormValue[1] },
      tags: { type: FormStructureType.FormControl, value: [] },
      // Public-facing provider profile fields (surfaced on the picaluna website / AEO).
      description: { type: FormStructureType.FormControl },
      yearFounded: { type: FormStructureType.FormControl },
      url: { type: FormStructureType.FormControl },
      contact: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormContactStructure),
        valueAs: FormStructureValueType.Contact,
      },
      address: {
        type: FormStructureType.FormGroup,
        value: _.cloneDeep(FormAddressStructure),
        valueAs: FormStructureValueType.Address,
      },
      services: {
        type: FormStructureType.FormArray,
        value: _.cloneDeep(FormServiceProviderServiceStructure),
      },
      isGlobal: { type: FormStructureType.FormControl, value: false },
    };

    delete formStructure.contact.relationToDeceased;

    const formGenerator = new FormGenerator();

    this.form = formGenerator.generate(formStructure, data);

    const services = this.form.get('services') as FormArray;

    for (let i = 0; i < services.length; i++) {
      const serviceWrapperFormGroup = services.at(i) as FormGroup;

      // Note: This is a special control. It's used to get a reference back to `this` when working with services in a form.
      // Don't remove this, or modify its use without talking to Ivan.
      let _serviceProvider = serviceWrapperFormGroup.get('_serviceProvider') as FormControl;

      if (_serviceProvider) {
        _serviceProvider.patchValue(this);
      } else {
        serviceWrapperFormGroup.addControl('_serviceProvider', new FormControl(this));
      }

      const service = new Service(this, serviceWrapperFormGroup, this.http);
      const type = serviceWrapperFormGroup.get('type')?.value;
      const serviceFormControl = serviceWrapperFormGroup.get('service') as FormControl;

      // Build the type-specific form (productCategories/products) only when a
      // type is actually set.
      if (type && type.value) {
        const serviceFormStructure = this.dynamicService.getDynamicFormStructure(type.value);

        if (serviceFormStructure) {
          formGenerator.generate(
            serviceFormStructure,
            _.get(data, `services.${i}.service`, {}),
            service.form
          );
        }
      }

      // ALWAYS patch the Service instance into the control — even for a service
      // with no type (a "pick a type later" service, or one left typeless by the
      // historical import/save bug). The save serialiser does
      // `service.service.form.getRawValue()`, so the control must hold a Service
      // (which has a `.form`), never the bare loaded value. A typeless service
      // keeps an empty `.form`; the Type selector still renders so the user can
      // choose a type whenever they want, and the products UI stays hidden until
      // they do. Previously this patch sat inside the `type` guard, so a typeless
      // service never became a Service instance and the next save crashed.
      if (serviceFormControl) {
        serviceFormControl.patchValue(service);
      }
    }

    // The form was just built from persisted data, so every service id present
    // now is one the server knows about.
    this.refreshPersistedServiceIds();
  }

  /**
   * Snapshot the service _meta.ids currently in the form as the persisted set.
   * Call this only when the form reflects what's on the server — i.e. right
   * after a load or a successful whole-provider save.
   */
  private refreshPersistedServiceIds(): void {
    this.persistedServiceIds = new Set<string>();
    const services = this.form?.get('services') as FormArray | null;
    if (!services) {
      return;
    }
    for (let i = 0; i < services.length; i++) {
      const id = (services.at(i) as FormGroup).get('_meta.id')?.value;
      if (id) {
        this.persistedServiceIds.add(id);
      }
    }
  }

  /**
   * Whether a service (by its _meta.id) exists in the persisted, server-side
   * provider. A freshly-added service the user hasn't saved yet returns false —
   * its products can't be PATCHed individually (the server has no such service
   * id) and must go through a whole-provider save first.
   */
  public isServicePersisted(serviceId: string): boolean {
    return !!serviceId && this.persistedServiceIds.has(serviceId);
  }

  private postDataAsForm(data: any): any {
    if (data.user) {
      const user = this.userService.getById(data.user);

      if (user) {
        data.user = user;
      } else {
        data.user = null;
      }
    }

    return data;
  }

  private formAsPostData() {
    const convertDates = (data: any, dates: any[]) => {
      for (let date of dates) {
        const d = _.get(data, date.path);

        if (moment.isDate(d)) {
          _.set(data, date.path, moment(d).format(environment.dbDateFormat));
        } else if (moment.isMoment(d)) {
          _.set(data, date.path, d.format(environment.dbDateFormat));
        } else {
          _.set(data, date.path, null);
        }
      }

      return data;
    };

    const convertServiceClassToForm = (data: any) => {
      if (_.has(data, 'services') && data.services.length) {
        data.services = _.map(data.services, (service) => {
          delete service._serviceProvider;
          service.service = service.service.form.getRawValue();
          return service;
        });
      }

      return data;
    };

    /**
     * Empty arrays (eg. []) / objects (eg. {}) cause problems when saving to the DB, so we remove them
     * Note: Maybe I should move this to the server?? For now it will be here...
     */
    const removeEmptyObjects = (data: any) => {
      let keyRemoved = false;

      const dotted = dot.dot(data);

      for (let key in dotted) {
        if (dotted.hasOwnProperty(key)) {
          if ((_.isArray(dotted[key]) || _.isObject(dotted[key])) && !_.isDate(dotted[key])) {
            keyRemoved = true;

            _.unset(data, key);
          }
        }
      }

      if (keyRemoved) {
        data = removeEmptyObjects(data);
      }

      return data;
    };

    let data = _.cloneDeep(this.form.value);

    delete data.isSubmitted;

    if (_.isEmpty(data.tags)) {
      delete data.tags;
    }

    data = convertDates(data, []);

    data = convertServiceClassToForm(data);

    if (data.user) {
      data.user = data.user.data.id;
    }

    // This must be last function called within this.formAsPostData()
    data = removeEmptyObjects(data);

    return data;
  }
}
